import React, { useCallback, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse, stringify } from 'qs'
import { Tooltip } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { CAMPAIGNS_TABLE_COLUMNS, DEFAULT_QUERY } from './campaigns-table.constants'
import { useCampaignsContext } from '../../contexts'
import { campaignsDataPreparation } from '../../logics'
import { CampaignStateSwitch, TableActions } from '../table-actions'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const CampaignsTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useSelector((state) => state.combos)
  const {
    data,
    pagination: { total_campanas: totalEntries },
    loading,
    search,
    setSelectedTableRows,
    getTableData,
    onClickTableRow,
  } = useCampaignsContext()

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`/campaigns?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      CAMPAIGNS_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.campaigns.table.columns.${column.field}.title` }),
          size: column.width,
        }

        if (column.field === 'activa' || column.field === 'actions') {
          return {
            ...baseColumn,
            muiTableBodyCellProps: { align: 'center' },
            Cell: ({ row }) =>
              column.field === 'activa' ? (
                <CampaignStateSwitch rowData={row.original} />
              ) : (
                <TableActions rowData={row.original} />
              ),
          }
        }

        return {
          ...baseColumn,
          muiTableBodyCellProps: {
            sx: {
              maxWidth: `${column.width}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
          Cell: ({ cell, row }) => {
            let value = cell.getValue()

            if (column.field === 'productos' || column.field === 'mercados') {
              value = Array.isArray(value) ? value.join(', ') : value
            } else if (column.comboId && combos[column.comboId]?.data) {
              const lookup = getLookup(combos[column.comboId].data)
              value = lookup[value] ?? value
            } else if (column.render && typeof column.render === 'function') {
              value = column.render(row.original)
            }

            const displayText = value ?? '--'
            return <Tooltip title={displayText}>{displayText}</Tooltip>
          },
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(() => {
    const result = campaignsDataPreparation({ data, combos, intl })
    return Array.isArray(result) ? result : []
  }, [data, combos, intl])

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: true,
      showSelectAllCheckbox: true,
      muiTableProps: { sx: { tableLayout: 'fixed' } },
      fixedColumns: { right: 2 },
      draggable: false,
    }),
    []
  )

  const handleRowClick = useCallback(
    (event, rowData) => {
      onClickTableRow(rowData?.id)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleRowClick}
      onSelectionChange={handleSelectRows}
    />
  )
}
